/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useCallback, useState } from 'react';

/* Import libraries */
import type { ParsedLastValueCsv } from '@/lib/timeSeries/parseLastValueCsv';
import type { ParsedIntervalValueCsv } from '@/lib/timeSeries/parseIntervalValueCsv';
import type { ParsedTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';

/* Import types */
import type { StoredPlotlyResponse } from '@/types/eeCheck';
import type {
    CurrentValueOutputState,
    IntervalValueOutputState,
    OutputMode,
    TabularOutputMode,
    TabularOutputState,
} from '@/types/ui';

export function useDemoOutputs() {
    const [plotlyFigure, setPlotlyFigure] = useState<StoredPlotlyResponse | null>(null);
    const [outputMode, setOutputMode] = useState<OutputMode>('empty');
    const [loginResponseText, setLoginResponseText] = useState('');
    const [loginOutputMode, setLoginOutputMode] = useState<TabularOutputMode>('empty');
    const [loginTimeSeriesPreview, setLoginTimeSeriesPreview] = useState<ParsedTimeSeriesCsv | null>(null);
    const [timeSeriesResponseText, setTimeSeriesResponseText] = useState('');
    const [timeSeriesOutputMode, setTimeSeriesOutputMode] = useState<TabularOutputMode>('empty');
    const [timeSeriesPreview, setTimeSeriesPreview] = useState<ParsedTimeSeriesCsv | null>(null);
    const [currentValueResponseText, setCurrentValueResponseText] = useState('');
    const [currentValueOutputMode, setCurrentValueOutputMode] = useState<CurrentValueOutputState['outputMode']>('empty');
    const [currentValuePreview, setCurrentValuePreview] = useState<ParsedLastValueCsv | null>(null);
    const [intervalValueResponseText, setIntervalValueResponseText] = useState('');
    const [intervalValueOutputMode, setIntervalValueOutputMode] = useState<IntervalValueOutputState['outputMode']>('empty');
    const [intervalValuePreview, setIntervalValuePreview] = useState<ParsedIntervalValueCsv | null>(null);

    const resetDemoOutputs = useCallback(() => {
        setPlotlyFigure(null);
        setLoginResponseText('');
        setLoginTimeSeriesPreview(null);
        setLoginOutputMode('empty');
        setCurrentValueResponseText('');
        setCurrentValuePreview(null);
        setCurrentValueOutputMode('empty');
        setIntervalValueResponseText('');
        setIntervalValuePreview(null);
        setIntervalValueOutputMode('empty');
        setTimeSeriesResponseText('');
        setTimeSeriesPreview(null);
        setTimeSeriesOutputMode('empty');
        setOutputMode('empty');
    }, []);

    const loginOutputState: TabularOutputState = {
        outputMode: loginOutputMode,
        responseText: loginResponseText,
        timeSeriesPreview: loginTimeSeriesPreview,
    };
    const timeSeriesOutputState: TabularOutputState = {
        outputMode: timeSeriesOutputMode,
        responseText: timeSeriesResponseText,
        timeSeriesPreview,
    };
    const currentValueOutputState: CurrentValueOutputState = {
        currentValuePreview,
        outputMode: currentValueOutputMode,
        responseText: currentValueResponseText,
    };
    const intervalValueOutputState: IntervalValueOutputState = {
        intervalValuePreview,
        outputMode: intervalValueOutputMode,
        responseText: intervalValueResponseText,
    };

    return {
        currentValueOutputState,
        intervalValueOutputState,
        loginOutputState,
        outputMode,
        plotlyFigure,
        resetDemoOutputs,
        setCurrentValueOutputMode,
        setCurrentValuePreview,
        setCurrentValueResponseText,
        setIntervalValueOutputMode,
        setIntervalValuePreview,
        setIntervalValueResponseText,
        setLoginOutputMode,
        setLoginResponseText,
        setLoginTimeSeriesPreview,
        setOutputMode,
        setPlotlyFigure,
        setTimeSeriesOutputMode,
        setTimeSeriesPreview,
        setTimeSeriesResponseText,
        timeSeriesOutputState,
    };
}

export type DemoOutputs = ReturnType<typeof useDemoOutputs>;
